/**
 * Token persistence. The refresh token is the durable credential (survives
 * reloads via localStorage); the short-lived access token is cached in memory
 * and also mirrored to storage so a reload can make authenticated calls
 * immediately without waiting for a refresh round-trip.
 *
 * Keys are namespaced `brainkey-client.*` (not `brainkey.*`, which the coach
 * portal uses) so a client session can never collide with a coach session if
 * both apps ever share a browser/origin — see docs/DECISIONS.md.
 */

const ACCESS_KEY = 'brainkey-client.accessToken';
const REFRESH_KEY = 'brainkey-client.refreshToken';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
}

let accessToken: string | null = null;

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage disabled (private mode / SSR) — degrade gracefully
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export const tokenStore = {
  getAccessToken(): string | null {
    if (accessToken !== null) return accessToken;
    accessToken = readStorage(ACCESS_KEY);
    return accessToken;
  },

  getRefreshToken(): string | null {
    return readStorage(REFRESH_KEY);
  },

  hasSession(): boolean {
    return this.getRefreshToken() !== null;
  },

  set(tokens: StoredTokens): void {
    accessToken = tokens.accessToken;
    writeStorage(ACCESS_KEY, tokens.accessToken);
    writeStorage(REFRESH_KEY, tokens.refreshToken);
  },

  clear(): void {
    accessToken = null;
    writeStorage(ACCESS_KEY, null);
    writeStorage(REFRESH_KEY, null);
  },
};
